"use client";

import React, { ReactNode } from "react";

import { Card, CardBody, CardHeader } from "@heroui/card";

import { SPACING } from "@/lib/constants/layoutConstants";
import { cn } from "@/lib/utils";

type CardPadding = "sm" | "md" | "lg";

interface StandardCardProps {
  /**
   * Card padding size
   * - sm: p-4 sm:p-6 (responsive, smaller on mobile)
   * - md: p-6
   * - lg: p-8
   */
  padding?: CardPadding;
  /**
   * Whether to show org color accent border
   */
  accent?: boolean;
  /**
   * Card header content (optional)
   */
  header?: ReactNode;
  /**
   * Card body content
   */
  children: ReactNode;
  /**
   * Additional CSS classes
   */
  className?: string;
  /**
   * Additional props to pass to Card component
   */
  [key: string]: any;
}

const PADDING_CLASS_MAP: Record<CardPadding, string> = {
  lg: SPACING.CARD_PADDING_LG,
  md: SPACING.CARD_PADDING_MD,
  sm: SPACING.CARD_PADDING_SM,
};

function getAccentClass(accent: boolean): string {
  return accent ? "border-l-4 border-l-primary" : "border border-default-200";
}

/**
 * Base card component with standardized padding, shadows, and optional org color accents
 * Provides consistent card styling across all pages
 */
export function StandardCard({
  accent = false,
  children,
  className,
  header,
  padding = "md",
  ...props
}: StandardCardProps) {
  const paddingClass = PADDING_CLASS_MAP[padding];
  const accentClass = getAccentClass(accent);

  return (
    <Card className={cn("shadow-sm", accentClass, className)} {...props}>
      {header && <CardHeader className={paddingClass}>{header}</CardHeader>}
      <CardBody className={paddingClass}>{children}</CardBody>
    </Card>
  );
}
